// ============================================================
// Sales module manifest — V2 platform's SECOND canary.
//
// Phase 6 (2026-04-24) — see ARCHITECTURE.md §2 (manifest format) and
// DESIGN_LOG entry "Phase 6 Sales: build the customer-facing lifecycle".
//
// Owners: erp-operations (lifecycles) + firebase-expert (Vite wiring) +
//         principal-architect (manifest contract).
//
// What this is the source of truth for:
//   - 5 Sell-section sidebar routes (customers + 4 lifecycle docs)
//   - 5 Firestore collections (customers, quotations, salesorders,
//     deliveryorders, customerinvoices) — R6 generates Rules from this list
//   - 5 permission strings in the sales namespace (R6 maps role→perms)
//   - 4 dashboard widget descriptors (R3+R4 mounts them; Phase 6 only DECLARES)
//   - 4 industry packs (manufacturing, trading, services, retail) — Sales
//     fits all of them; even a Services pack ships Quotations+Invoices
//     (services companies still quote and invoice; they just don't ship goods)
//
// Dependencies: ['inventory'] — Sales DO posting calls inventory's
// commitStockMovement. Registry's dependency validator hard-fails boot
// if 'inventory' is not in tenantConfig.enabledModules before 'sales'.
//
// Relationship to `invoicing` module (A2 2026-04-25)
// ──────────────────────────────────────────────────
// This Sales module is a SUPERSET of the `invoicing` module. The
// invoicing module declares `supersededBy: ['sales']` — when both are
// enabled on the same tenant, the registry's getRoutesFor() drops
// invoicing's customers + customer-invoices routes (sales already
// provides them, with extras). Services-pack tenants get only
// invoicing; manufacturing/trading/retail tenants get only sales.
//
// Both modules write the SAME Firestore collections (`customers`,
// `customerinvoices`). Firestore Rules accept either `sales.write` or
// `invoicing.write` to gate writes (canManageBilling helper). See
// DESIGN_LOG A2 for the full design debate.
//
// IMPORTANT: changes to `id` are BREAKING.
// ============================================================

export default {
  id:          'sales',
  name:        'Sales',
  version:     '1.0.0',
  description: 'Customer master + quotation → SO → DO → invoice lifecycle.',

  // Lazy load — Vite emits a separate chunk; tenants without 'sales' enabled
  // never download it. Inventory canary measured a 21.63 kB / 7.14 kB gzip
  // chunk; Sales should be larger because it has 5 routes vs 1.
  load: () => import('./index.js'),

  // Sidebar contributions — all in the new 'sell' section that main.js
  // adds between 'procure' and 'people'.
  // 2026-05-04 sidebar-leak fix — every sell route gates on `sales.read`.
  // Roles that get it: director + admin (via '*'/__ALL_EXCEPT_TENANT_CONFIG__),
  // sales (via `sales.*` wildcard expansion). Staff / shop-floor / production-
  // supervisor / hr do NOT see Sell. Per-action permissions (sales.deliver,
  // sales.invoice, etc.) are enforced inside the page — sidebar is read-gate only.
  routes: [
    {
      id:       'customers',
      label:    'Customers',
      icon:     '👥',
      section:  'sell',
      sidebarPosition: 10,
      visible:  (role, hasPerm) => hasPerm('sales.read'),
      mount:    'mountCustomersList',
      unmount:  'unmountCustomersList'
    },
    {
      id:       'quotations',
      label:    'Quotations',
      icon:     '📋',
      section:  'sell',
      sidebarPosition: 20,
      visible:  (role, hasPerm) => hasPerm('sales.read'),
      mount:    'mountQuotationsList',
      unmount:  'unmountQuotationsList'
    },
    {
      id:       'sales-orders',
      label:    'Sales Orders',
      icon:     '🛒',
      section:  'sell',
      sidebarPosition: 30,
      visible:  (role, hasPerm) => hasPerm('sales.read'),
      mount:    'mountSalesOrdersList',
      unmount:  'unmountSalesOrdersList'
    },
    {
      id:       'delivery-orders',
      label:    'Delivery Orders',
      icon:     '🚚',
      section:  'sell',
      sidebarPosition: 40,
      visible:  (role, hasPerm) => hasPerm('sales.read'),
      mount:    'mountDeliveryOrdersList',
      unmount:  'unmountDeliveryOrdersList'
    },
    {
      id:       'customer-invoices',
      label:    'Customer Invoices',
      icon:     '🧾',
      section:  'sell',
      sidebarPosition: 50,
      visible:  (role, hasPerm) => hasPerm('sales.read'),
      mount:    'mountCustomerInvoicesList',
      unmount:  'unmountCustomerInvoicesList'
    }
  ],

  // Firestore collections this module reads/writes (R6 will use this list
  // to auto-generate the matching Rules block).
  schemas: [
    'customers',
    'quotations',
    'salesorders',
    'deliveryorders',
    'customerinvoices'
  ],

  // Permission strings the module declares. R6 maps roles → permissions
  // per industry. Today the UI checks role directly (SALES_ROLES set) and
  // Rules enforce server-side.
  permissions: [
    'sales.read',     // see the sales lists
    'sales.write',    // create/edit Customers + Quotations + SOs + Invoices
    'sales.confirm',  // confirm an SO (Draft → Confirmed)
    'sales.deliver',  // post a DO (commit Stock-Out)
    'sales.cancel',   // cancel a Posted DO (reverses stock; audit-trailed) — Phase 6.A2
    'sales.invoice'   // create + send + print Customer Invoices
    // Note: hard-delete a DO is hardcoded in Rules to director|admin (no perm
    // string) — too dangerous for a wildcard pack to grant.
  ],

  // 'sales' depends on 'inventory' (DO posting calls commitStockMovement).
  // Registry validates: if 'inventory' is not enabled, boot fails fast.
  dependencies: ['inventory'],

  // Industry packs that include this module by default.
  // Even Services ships Sales (services firms quote + invoice; they just
  // don't ship goods — the DO module stays present but unused).
  fitsIndustries: ['manufacturing', 'trading', 'services', 'retail'],

  // Cloud Functions this module needs (Blaze-plan deployments only).
  // None today — DO posting fits inside one writeBatch (per-line, like GRN).
  functions: [],

  // Dashboard widgets this module contributes.
  // Declarations DEFERRED (silent-sweep 2026-04-26): widget mount functions
  // were never built — declaring them here causes dashboard.js to log a
  // console.warn for each on every page load. Stub-out to silence the
  // noise; uncomment + build src/modules/sales/widgets.js when ready.
  // See d:/ERP/v2/PARITY.md "R4 deferrals" + DESIGN_LOG R3 widget-comp section.
  dashboardWidgets: []
  // [
  //   { id: 'sales-open-quotations',     label: 'Open quotations',     defaultRoles: ['director','admin','sales'] },
  //   { id: 'sales-pending-deliveries',  label: 'Pending deliveries',  defaultRoles: ['director','admin','sales','warehouse'] },
  //   { id: 'sales-unpaid-invoices',     label: 'Unpaid invoices',     defaultRoles: ['director','admin','accounts'] },
  //   { id: 'sales-monthly-revenue',     label: 'This month revenue',  defaultRoles: ['director','admin','accounts'] }
  // ]
};
